import { Shield, CheckCircle2, Phone, Mail, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const WarrantyPage = () => {
  const { t } = useTranslation();
  const warrantyFeatures = [
    {
      title: t('warranty.powertrainTitle'),
      description: t('warranty.powertrainDesc'),
      items: [t('warranty.powertrain1'), t('warranty.powertrain2'), t('warranty.powertrain3'), t('warranty.powertrain4')]
    },
    {
      title: t('warranty.electricalTitle'),
      description: t('warranty.electricalDesc'),
      items: [t('warranty.electrical1'), t('warranty.electrical2'), t('warranty.electrical3'), t('warranty.electrical4')]
    },
    {
      title: t('warranty.roadAssistTitle'),
      description: t('warranty.roadAssistDesc'),
      items: [t('warranty.roadAssist1'), t('warranty.roadAssist2'), t('warranty.roadAssist3'), t('warranty.roadAssist4')]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="warranty-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1920&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-2xl">
            <p className="text-[#3b82f6] font-semibold mb-3 text-sm">{t('warranty.heroTag')}</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('warranty.heroTitle')} <span className="text-[#3b82f6]">{t('warranty.heroTitleHighlight')}</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              {t('warranty.heroDesc')}
            </p>
            <div className="flex items-center gap-4">
              <Shield className="h-12 w-12 text-[#3b82f6]" />
              <div>
                <p className="text-white font-semibold">{t('warranty.certifiedWarranty')}</p>
                <p className="text-slate-400 text-sm">{t('warranty.allCovered')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coverage Features */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('warranty.coverageTitle')} <span className="text-[#3b82f6]">{t('warranty.coverageTitleHighlight')}</span>
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">
              {t('warranty.coverageSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {warrantyFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
                <div className="bg-[#3b82f6]/10 rounded-xl p-4 w-fit mb-6">
                  <Shield className="h-8 w-8 text-[#3b82f6]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-500 mb-6">{feature.description}</p>
                <ul className="space-y-3">
                  {feature.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('warranty.faqTitle')} <span className="text-[#3b82f6]">{t('warranty.faqTitleHighlight')}</span>
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-2">{t('warranty.faqQ1')}</h3>
              <p className="text-slate-400">{t('warranty.faqA1')}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-2">{t('warranty.faqQ2')}</h3>
              <p className="text-slate-400">{t('warranty.faqA2')}</p>
            </div>
            <div className="bg-slate-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-2">{t('warranty.faqQ3')}</h3>
              <p className="text-slate-400">{t('warranty.faqA3')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#3b82f6]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('warranty.ctaTitle')}
          </h2>
          <p className="text-blue-100 mb-8">
            {t('warranty.ctaDesc')}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="tel:+18339956477" className="flex items-center gap-2 bg-white text-[#3b82f6] px-6 py-3 rounded-lg font-semibold hover:bg-slate-100 transition-colors">
              <Phone className="h-5 w-5" />
              (833) 995-6477
            </a>
            <a href="mailto:info@entrepotautomontreal.ca" className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
              <Mail className="h-5 w-5" />
              {t('warranty.writeUs')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WarrantyPage;
